import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { fetchOrderById, updateOrderStatus } from "../store/actions/orderActions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { ArrowLeft, MoreVertical, Eye } from "lucide-react";
import { formatCurrency } from "../utils/formatCurrency";
import * as ratesService from "../services/ratesService";

const Title = styled.h2`
  margin: 4px 0 0;
  font-size: 24px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Muted = styled.p`
  margin: 2px 0 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.body};
`;

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { order: orderFromState, loading, error } = useSelector((state) => state.order);

  useEffect(() => {
    if (orderId) dispatch(fetchOrderById(orderId));
  }, [dispatch, orderId]);

  const order = orderFromState || null;
  const items = order?.lineItems || order?.orderItems || [];
  const orderCur = order?.currency || "USD";
  const [reportCurrency, setReportCurrency] = useState("USD");
  const [rates, setRates] = useState(null);

  useEffect(() => {
    ratesService.getRates().then((r) => setRates(r?.rates || null)).catch(() => setRates(null));
  }, []);

  const fmt = (amt) => {
    if (amt == null) return "—";
    const v = Number(amt);
    if (rates && orderCur !== reportCurrency) {
      return formatCurrency(ratesService.convert(v, orderCur, reportCurrency, rates), reportCurrency);
    }
    return formatCurrency(v, orderCur);
  };

  const customerName = order?.customerId?.name || order?.customerName || order?.customerId?.email || "—";
  const customerEmail = order?.customerId?.email || order?.email || "—";
  const orderDate = order?.createdAt || order?.date || order?.orderDate ? (typeof order?.createdAt === "string" || typeof order?.date === "string" ? new Date(order?.createdAt || order?.date).toLocaleDateString() : String(order?.createdAt || order?.date || order?.orderDate)) : "—";
  const total = order?.total ?? order?.totalAmount ?? 0;

  if (loading && !order) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/orders")}><ArrowLeft size={16} /> Back to Orders</Button>
        <p style={{ color: "#6b7280" }}>Loading order...</p>
      </div>
    );
  }
  if (error && !order) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/orders")}><ArrowLeft size={16} /> Back to Orders</Button>
        <p style={{ color: "#dc2626" }}>{error}</p>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/orders")}><ArrowLeft size={16} /> Back to Orders</Button>
        <p style={{ color: "#6b7280" }}>Order not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
        <div>
          <Button
            $variant="ghost"
            $size="sm"
            onClick={() => navigate("/orders")}
          >
            <ArrowLeft size={16} />
            Back to Orders
          </Button>
          <Title>Order #{order.orderId || orderId}</Title>
          <Muted>{orderDate}</Muted>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <select value={reportCurrency} onChange={(e) => setReportCurrency(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13 }}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: 9999,
              fontSize: 13,
              fontWeight: 700,
              background: (order.status === "completed" || order.status === "delivered") ? "#dcfce7" : order.status === "refunded" || order.payment === "refunded" ? "#fee2e2" : "#fef3c7",
              color: (order.status === "completed" || order.status === "delivered") ? "#166534" : order.status === "refunded" || order.payment === "refunded" ? "#b91c1c" : "#92400e",
            }}
          >
            {order.status || "pending"}
          </span>
          {order.fulfillmentStatus && (
            <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, background: "#f3f4f6", color: "#374151" }}>
              Fulfillment: {order.fulfillmentStatus}
            </span>
          )}
          {order.financialStatus && (
            <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, background: "#f3f4f6", color: "#374151" }}>
              Payment: {order.financialStatus}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gap: 20 }}>
        <Card style={{ padding: 20 }}>
          <h3
            style={{
              margin: 0,
              marginBottom: 12,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            Customer Information
          </h3>
          <p style={{ margin: 0, fontWeight: 600 }}>{customerName}</p>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: "#6b7280" }}>
            {customerEmail}
          </p>
        </Card>

        <Card style={{ padding: 0 }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead style={{ textAlign: "right" }}>Price</TableHead>
                <TableHead style={{ textAlign: "right" }}>Total</TableHead>
                <TableHead style={{ textAlign: "right" }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={5} style={{ color: "#6b7280" }}>No items</TableCell></TableRow>
              ) : items.map((item) => {
                const productId = item.productId || item.product?._id;
                const name = item.name || item.product?.name || "—";
                const qty = item.quantity ?? item.qty ?? 0;
                const price = item.price ?? item.unitPrice ?? 0;
                const lineTotal = item.total ?? item.subtotal ?? (qty * price);
                return (
                  <TableRow key={item.id || item._id}>
                    <TableCell>{name}</TableCell>
                    <TableCell>{qty}</TableCell>
                    <TableCell style={{ textAlign: "right" }}>{fmt(price)}</TableCell>
                    <TableCell style={{ textAlign: "right" }}>{fmt(lineTotal)}</TableCell>
                    <TableCell style={{ textAlign: "right" }}>
                      {productId && (
                        <Button $variant="outline" $size="sm" style={{ marginRight: 6 }} onClick={() => navigate(`/products/${productId}`)}>
                          <Eye size={14} /> Product
                        </Button>
                      )}
                      <Button $variant="ghost" $size="icon"><MoreVertical size={16} /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        <Card style={{ padding: 20 }}>
          <h3 style={{ margin: 0, marginBottom: 10, fontSize: 18, fontWeight: 700 }}>Order Summary</h3>
          <div style={{ display: "grid", gap: 6 }}>
            {(order.subtotal != null && order.subtotal > 0) && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Subtotal</span>
                <span>{fmt(order.subtotal)}</span>
              </div>
            )}
            {!(order.subtotal != null && order.subtotal > 0) && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Subtotal</span>
                <span>{fmt(total)}</span>
              </div>
            )}
            {(order.discountTotal != null && order.discountTotal > 0) && (
              <div style={{ display: "flex", justifyContent: "space-between", color: "#059669" }}>
                <span>Discount</span>
                <span>-{fmt(order.discountTotal)}</span>
              </div>
            )}
            {(order.discountCodes && order.discountCodes.length > 0) && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {order.discountCodes.map((d, i) => `${d.code}${d.amount ? ` (${fmt(d.amount)})` : ""}`).join(", ")}
              </div>
            )}
            {(order.shippingLines && order.shippingLines.length > 0) ? (
              order.shippingLines.map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{s.title || "Shipping"}</span>
                  <span>{fmt(s.amount || 0)}</span>
                </div>
              ))
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Shipping</span>
                <span>{order.shipping != null ? fmt(order.shipping) : "—"}</span>
              </div>
            )}
            {(order.taxTotal != null && order.taxTotal > 0) && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Tax</span>
                <span>{fmt(order.taxTotal)}</span>
              </div>
            )}
            {(order.refunds && order.refunds.length > 0) && (
              <>
                {order.refunds.map((r, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", color: "#b91c1c" }}>
                    <span>Refund{r.reason ? `: ${r.reason}` : ""}</span>
                    <span>-{fmt(r.amount || 0)}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", color: "#b91c1c", fontWeight: 600 }}>
                  <span>Total refunded</span>
                  <span>-{fmt(order.refunds.reduce((s, r) => s + (r.amount || 0), 0))}</span>
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 18, marginTop: 4 }}>
              <span>Total</span>
              <span>{fmt(total)}</span>
            </div>
          </div>
          {order.note && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>Customer note</p>
              <p style={{ margin: "4px 0 0", fontSize: 14 }}>{order.note}</p>
            </div>
          )}
          {(order.shippingAddress || order.billingAddress) && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb", display: "grid", gap: 8 }}>
              {order.shippingAddress && (
                <div>
                  <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>Shipping</p>
                  <p style={{ margin: "4px 0 0", fontSize: 14 }}>{typeof order.shippingAddress === "string" ? order.shippingAddress : [order.shippingAddress?.address1, order.shippingAddress?.city, order.shippingAddress?.country].filter(Boolean).join(", ")}</p>
                </div>
              )}
              {order.billingAddress && (
                <div>
                  <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>Billing</p>
                  <p style={{ margin: "4px 0 0", fontSize: 14 }}>{typeof order.billingAddress === "string" ? order.billingAddress : [order.billingAddress?.address_1 || order.billingAddress?.address1, order.billingAddress?.city, order.billingAddress?.country].filter(Boolean).join(", ")}</p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

